import { makeAutoObservable } from 'mobx'
import { Box, type IDropdownItem, type ITooltipProps, TooltipLink, Typography } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { roleLabelsMap, EnumUserRoles, type IUser } from 'entities/Users'
import { type AccessRequest } from 'entities/Users/model/AccessRequest'
import { NumbersPath } from 'entities/ExportRouters'
import {
  noManageInboxesPermissionTooltipLabel,
  noManageBillingPermissionTooltipLabel,
} from 'entities/UsersPermissions'
import { type SettingsStore } from 'pages/settings/store/settingsStore'
import { AssignNewInboxStore } from 'pages/settings/pages/members/ui/AssignMemberToInbox/store/assignNewInboxStore'
import { type IAssignInboxOption } from 'pages/settings/pages/members/ui/AssignMemberToInbox/ui/ChooseAssignInboxType/ChooseAssignInboxType'
import { AssignExistingInboxStore } from './assignExistingInboxStore'
import type { IAssignFlow } from './flows'

export type AssignInboxType = 'exist' | 'new'
export type AssignMemberMode = 'simple' | 'inboxAccess' | 'salesmsgAccess'

export type IAssignMemberToInboxConfig = {
  entities: IEntitiesStore
  settings: SettingsStore
  mode: AssignMemberMode
  onCancel: () => void
  onReject?: () => void
  flow: IAssignFlow
}

export const availableRolesForInvite: EnumUserRoles[] = [
  EnumUserRoles.ADMIN,
  EnumUserRoles.MANAGER,
  EnumUserRoles.MEMBER,
  EnumUserRoles.VIEW_ONLY,
]

export class AssignMemberToInboxStore {
  private _selectedMember: IUser | null = null
  private _accessRequest: AccessRequest | null = null

  role: EnumUserRoles = EnumUserRoles.MEMBER
  inboxType: AssignInboxType = 'exist'
  disabledNewInboxTooltip: ITooltipProps | null = null
  disabledExistInboxTooltip: ITooltipProps | null = null

  readonly mode: AssignMemberMode
  readonly onCancel: () => void
  readonly onReject?: (() => void) | null = null
  readonly assignNewInboxStore: AssignNewInboxStore
  readonly assignToExistingInboxStore: AssignExistingInboxStore

  constructor(private readonly _config: IAssignMemberToInboxConfig) {
    this.mode = _config.mode
    this.onCancel = _config.onCancel
    this.onReject = _config.onReject

    this.assignNewInboxStore = new AssignNewInboxStore(_config.entities, _config.settings)
    this.assignNewInboxStore.setFlow(_config.flow)

    this.assignToExistingInboxStore = new AssignExistingInboxStore(_config.entities)
    this.assignToExistingInboxStore.setFlow(_config.flow)

    makeAutoObservable(this)
  }

  get roleItems(): IDropdownItem[] {
    return availableRolesForInvite.map((role) => ({
      id: role,
      label: roleLabelsMap[role],
    }))
  }

  get isNewInboxType() {
    return this.inboxType === 'new'
  }

  get isLastStep() {
    if (!this.isNewInboxType) return true
    return this.assignNewInboxStore.isLastStep
  }

  get isFirstStep() {
    if (!this.isNewInboxType) return true
    return this.assignNewInboxStore.isFirstStep
  }

  get isSalesmsgAccessMode() {
    return this.mode === 'salesmsgAccess'
  }

  get isReviewMode() {
    return this.mode === 'inboxAccess' || this.isSalesmsgAccessMode
  }

  get isSeatRequired() {
    return this.isSalesmsgAccessMode && this.role !== EnumUserRoles.VIEW_ONLY
  }

  get selectedMember() {
    return this._selectedMember
  }

  get accessRequest() {
    return this._accessRequest
  }

  get isBillingSettingsAccess() {
    return this._config.entities.userPermissionsStore.can_manage_billing_settings
  }

  get isManageInboxesAccess() {
    return this._config.entities.userPermissionsStore.getItem('can_manage_organization_inboxes')
  }

  get seatsAvailable() {
    return this._config.entities.subscriptionStore.seatsAvailable
  }

  get isNewInboxDisabled() {
    if (this._config.entities.organizationStore.isNumbersGracePeriod) {
      this.disabledNewInboxTooltip = {
        type: 'description',
        label: (
          <Box display='flex' flexDirection='column' gap={0.5}>
            <Typography
              variant={'body-md-regular'}
              color={'var(--content-primary-primary-inverted)'}
              ariaLabel={'Grace Period'}
            >
              Take care of unpaid licenses
              <br /> before adding new numbers.
            </Typography>
          </Box>
        ),
        width: 233,
        placement: 'top',
        desc: (
          <TooltipLink
            text='Manage numbers'
            onClick={() => {
              uiStore.changeRoute({
                path: `${NumbersPath.root}`,
              })
            }}
          />
        ),
      }
      return true
    }

    if (!this.isBillingSettingsAccess) {
      this.disabledNewInboxTooltip = {
        label: noManageBillingPermissionTooltipLabel,
        placement: 'top',
        breakWord: true,
      }
      return true
    }

    if (!this.isManageInboxesAccess) {
      this.disabledNewInboxTooltip = {
        label: noManageInboxesPermissionTooltipLabel,
        placement: 'top',
        breakWord: true,
      }
      return true
    }

    return false
  }

  get allInboxes() {
    return this._config.entities.inboxesStore.organizationInboxes
  }

  get memberInboxIds() {
    if (!this.selectedMember) return []
    const member = this.selectedMember

    return this.allInboxes
      .filter((inbox) => inbox.memberIds.includes(member.id))
      .map((inbox) => inbox.id)
  }

  get isExistInboxDisabled() {
    if (!this.selectedMember) return false
    return this.allInboxes.length > 0 && this.memberInboxIds.length === this.allInboxes.length
  }

  get inboxOptions(): IAssignInboxOption[] {
    return [
      {
        value: 'exist',
        title: 'Assign to existing inbox',
        icon: 'inboxes2',
        disabled: this.isExistInboxDisabled,
        disabledTooltip: this.isExistInboxDisabled
          ? {
              type: 'description',
              label: 'Member is already assigned to all available inboxes',
            }
          : null,
      },
      {
        value: 'new',
        title: 'Assign to new inbox',
        icon: 'plusCircle',
        disabled: this.isNewInboxDisabled,
        disabledTooltip: this.disabledNewInboxTooltip,
      },
    ]
  }

  handleRoleChange = (value: EnumUserRoles) => {
    this.role = value
  }

  handleAssignInboxTypeChange = (value: AssignInboxType) => {
    this.inboxType = value
  }

  setMember = (member: IUser) => {
    this._selectedMember = member
    this.assignNewInboxStore.setSelectedToAssignInboxMember(member)
    void this.assignToExistingInboxStore.filterAvailableInboxes(this.memberInboxIds)
    this.inboxType = this.isExistInboxDisabled ? 'new' : 'exist'
  }

  setAccessRequestData = (accessRequest: AccessRequest, member?: IUser) => {
    this._accessRequest = accessRequest

    if (member) {
      this.setMember(member)
    } else {
      void this.assignToExistingInboxStore.filterAvailableInboxes()
    }
  }

  reset = () => {
    this._accessRequest = null
    this._selectedMember = null
    this.inboxType = 'exist'
    this.role = EnumUserRoles.MEMBER
    this.assignNewInboxStore.reset()
    this.assignToExistingInboxStore.reset()
  }
}
